import React, { useState } from "react";
import { createPortal } from "react-dom";
import clsx from "clsx";
import styles from "./styles.module.scss";
import WheelDateTimePicker, { type DateTimeValue } from "../wheel-datetime-picker";

interface Props extends Omit<React.InputHTMLAttributes<HTMLInputElement>, 'onChange'> {
  error?: boolean;
  onChange?: (value: string) => void;
  value?: string;
}

// value(react-hook-form等から復元された日時文字列)をピッカーの初期選択値に
// 変換する。これが無いと、既に値が保存されている状態でピッカーを開いた際、
// 保存済みの日時ではなく常に今日の日付から始まってしまう。
export function parseValueToPickerValue(value?: string): DateTimeValue | undefined {
  if (!value) return undefined;
  const date = new Date(value);
  if (Number.isNaN(date.getTime())) return undefined;
  return {
    year: String(date.getFullYear()),
    month: String(date.getMonth() + 1).padStart(2, "0"),
    day: String(date.getDate()).padStart(2, "0"),
    hour: String(date.getHours()).padStart(2, "0"),
    minute: String(date.getMinutes()).padStart(2, "0"),
  };
}

const DateTimeInput = React.forwardRef<HTMLInputElement, Props>(
  ({ className, error, onChange, value, placeholder = "", ...props }, ref) => {
    const [isPickerOpen, setIsPickerOpen] = useState(false);
    const [pickerValue, setPickerValue] = useState<DateTimeValue | undefined>();

    const handleInputClick = (e: React.MouseEvent) => {
      e.preventDefault();
      setIsPickerOpen(true);
    };

    const handlePickerChange = (newValue: DateTimeValue) => {
      setPickerValue(newValue);
      const isoString = `${newValue.year}-${newValue.month}-${newValue.day}T${newValue.hour}:${newValue.minute}`;
      onChange?.(isoString);
    };

    const formatDisplayValue = (val: DateTimeValue) => {
      return `${val.year}年${val.month}月${val.day}日 ${val.hour}:${val.minute}`;
    };

    const displayValue = pickerValue 
      ? formatDisplayValue(pickerValue)
      : value 
        ? (() => {
            try {
              const date = new Date(value);
              return `${date.getFullYear()}年${String(date.getMonth() + 1).padStart(2, '0')}月${String(date.getDate()).padStart(2, '0')}日 ${String(date.getHours()).padStart(2, '0')}:${String(date.getMinutes()).padStart(2, '0')}`;
            } catch {
              return value;
            }
          })()
        : '';

    return (
      <>
        <input
          ref={ref}
          type="text"
          className={clsx(styles.input, error && styles.error, className)}
          value={displayValue}
          placeholder={placeholder}
          onClick={handleInputClick}
          readOnly
          {...props}
        />
        {isPickerOpen && createPortal(
          <WheelDateTimePicker
            isOpen={isPickerOpen}
            onClose={() => setIsPickerOpen(false)}
            onChange={handlePickerChange}
            value={pickerValue ?? parseValueToPickerValue(value)}
            mode="datetime"
          />,
          document.querySelector('[data-bottom-sheet-content]') || document.body
        )}
      </>
    );
  }
);

DateTimeInput.displayName = "DateTimeInput";

export { DateTimeInput };
